/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useMemo, useState } from 'react';
import { Tag, TagGroup } from '@fluentui/react-tags';
import {
  makeStyles,
  tokens,
  mergeClasses,
  shorthands,
  Link,
} from '@fluentui/react-components';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';

const useStyles = makeStyles({
  tagGroup: {
    marginBottom: tokens.spacingVerticalM,
    flexWrap: 'wrap',
    ...shorthands.gap(tokens.spacingHorizontalXS),
  },
});

type TagsProps = {
  tagItems: string[];
  onChange: (tags: string[]) => void;
  onRenderTagContent: (tag: string) => React.ReactNode;
  i18n: {
    getString: (key: string) => string;
  };
  tagViewLimit?: number;
};

export const Tags = ({
  tagItems,
  onChange,
  onRenderTagContent,
  i18n,
  tagViewLimit,
}: TagsProps) => {
  const classes = useStyles();
  const [limitView, setLimitView] = useState(!!tagViewLimit);
  const removeItem = (_ev, { value }) => {
    onChange([...tagItems].filter((tag) => tag !== value));
  };

  const tagRenderList = useMemo(() => {
    if (tagViewLimit && tagItems.length > tagViewLimit && limitView) {
      return tagItems.slice(0, tagViewLimit);
    }
    return tagItems;
  }, [tagItems, tagViewLimit, limitView]);

  if (tagItems.length === 0) {
    return null;
  }

  const renderViewControl = () => {
    if (!tagViewLimit || tagItems.length <= tagViewLimit) {
      return null;
    }

    const clampCount = tagItems.length - tagRenderList.length;
    const buttonText = clampCount > 0 ? curlyTemplate(i18n.getString(_TL_('Show more ({{count}})')))({ count: clampCount }) : i18n.getString(_TL_('Show less'));

    return (
      <Link
        className={clampCount > 0 ? 'tag-more' : 'tag-less'}
        onClick={() => setLimitView(clampCount <= 0)}
      >
        {buttonText}
      </Link>
    );
  };

  return (
    <TagGroup
      className={mergeClasses('tag-group', classes.tagGroup)}
      onDismiss={removeItem}
      aria-label={i18n.getString(_TL_('selected items'))}
    >
      {tagRenderList.map((tag) => (
        <Tag
          dismissible
          className={mergeClasses('tag')}
          dismissIcon={{ 'aria-label': i18n.getString(_TL_('remove')) }}
          value={tag}
          key={tag}
        >
          { onRenderTagContent ? onRenderTagContent(tag) : tag}
        </Tag>
      ))}
      { renderViewControl() }
    </TagGroup>
  );
};
